import { useNavigate } from "react-router-dom";
import { useAppState } from "../store/contextProvider";
import { AppActions } from "../store/reducers";
import { Config } from "../../config/config";
import useCookie from "./useCookie";
import { closeSharedSocket } from "./sharedSocket";

// One logout at a time - several API calls can fail with an expired session together.
let logoutInProgress = false;

/**
 * The one logout flow - used by the profile menu, by AppInit before it sends the user to login, and
 * when an API call reports the session invalid/expired (useDataFetch). Clears everything:
 * the server session (HttpOnly cookie, Partner session store and its trend cache - account/logout),
 * the WebSocket, the UI cookies, saved configs and the in-memory session; then goes to login.
 */
export const useLogout = () => {
  const navigate = useNavigate();
  const { config, updateStore } = useAppState();
  const { clearCookies } = useCookie();

  async function logout() {
    if (logoutInProgress) return;
    logoutInProgress = true;
    try {
      const apiBaseUrl = config.env?.API_BASE_URL || config.env?.ORIGINAL_API_BASE_URL;
      try {
        await fetch(`${apiBaseUrl}${Config.ITradeAPI.baseUrl}/${Config.ITradeAPI.logout}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        });
      } catch (_) {
        // API unreachable - still clear everything on this side.
      }
      const hadSocket = closeSharedSocket();
      clearCookies();
      localStorage.removeItem("configs");
      updateStore({ type: AppActions.AddSession, payload: { isValidSession: false, userDetails: null } });
      updateStore({ type: AppActions.updatePageState, payload: { pageError: null } });
      // Short delay so server can process WebSocket close before next login
      if (hadSocket) await new Promise((resolve) => setTimeout(resolve, 300));
      navigate("/login");
    } finally {
      logoutInProgress = false;
    }
  }

  return { logout };
};
